import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { injectIntl, intlShape } from 'react-intl';
import classNames from 'classnames';
import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import { withStyles } from '@material-ui/core/styles';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import VisibilityIcon from '@material-ui/icons/Visibility';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import DoneALlIcon from '@material-ui/icons/DoneAll';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';

import TablePaginationActions from 'components/TablePaginationActions';
import TableHead from 'components/TableHead';
import TableCell from 'components/TableCell';
import FormField from 'components/FormField';
import validator from 'utils/validator/core';
import checkIfSubmitForm from 'utils/checkIfSubmitForm';
import { classesShape } from 'utils/shapes/classesShape';
import { itemShape } from 'utils/shapes/select.shapes';
import { Tooltip } from '@material-ui/core';
import surveysStyles from './Surveys.styles';

import messages from './Surveys.messages';

const validators = [{
  fieldName: 'search',
  validators: [{
    validatorName: 'maxLength',
    parameters: [255],
  }],
}];

const getFromMeta = (value, meta) => {
  const item = _find(meta, (el) => el.id === value);

  return item.name || value;
};

const Surveys = ({
  onAddClick, classes, intl, initialValues, onSearchSubmit, statuses, items,
  totalItemsCount, page, rowsPerPageOptions, rowsPerPage, groups,
  onChangePage, onChangeRowsPerPage, onDelete, onEdit, sortingDirection,
  sortingField, onChangeSort, onPreview, surveyTypes, surveyRespondents, onCopy,
  onDownloadSchema, onDownloadCsv, onDownloadResults,
}) => {
  const head = [{
    id: 'number',
    label: '#',
    class: classes.numberCell,
    sortable: false,
  }, {
    id: 'id',
    label: intl.formatMessage(messages.id),
    class: classes.idCell,
    sortable: true,
  }, {
    id: 'name',
    label: intl.formatMessage(messages.name),
    class: classes.nameCell,
    sortable: true,
  }, {
    id: 'surveyRespondent',
    label: intl.formatMessage(messages.surveyRespondent),
    class: classes.surveyRespondentCell,
    sortable: true,
  }, {
    id: 'surveyType',
    label: intl.formatMessage(messages.surveyType),
    class: classes.surveyTypeCell,
    sortable: true,
  }, {
    id: 'group',
    label: intl.formatMessage(messages.group),
    class: classes.groupCell,
    sortable: true,
  }, {
    id: 'status',
    label: intl.formatMessage(messages.status),
    class: classes.statusCell,
    sortable: true,
  }, {
    id: 'questionsCount',
    label: intl.formatMessage(messages.questionsCount),
    class: classes.questionsCountCell,
    sortable: true,
  }, {
    id: 'maxTime',
    label: intl.formatMessage(messages.maxTime),
    class: classes.maxTimeCell,
    sortable: true,
  }, {
    id: 'actions',
    label: intl.formatMessage(messages.actions),
    class: classes.actionsCell,
    sortable: false,
  }];

  return (
    <React.Fragment>
      <div className={classes.title}>
        <Typography variant="h6">
          {intl.formatMessage(messages.surveysList)}
        </Typography>
      </div>
      <Formik
        initialValues={
          { ...initialValues }
        }
        validate={(values) => validator(values, validators, intl.formatMessage)}
        onSubmit={onSearchSubmit}
        render={({
          values,
          errors,
          touched,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          dirty,
          handleReset,
          isSubmitting,
          submitCount,
          setSubmitting,
        }) => (
          <form onSubmit={(e) => { checkIfSubmitForm(e, handleSubmit, !dirty || isSubmitting); }}>
            <div className={classes.filtersWrapper}>
              <div className={classes.searchWrapper}>
                <FormField
                  onBlur={setFieldTouched}
                  onChange={setFieldValue}
                  errors={errors}
                  touched={touched}
                  setSubmitting={setSubmitting}
                  name="search"
                  label={intl.formatMessage(messages.findSurvey)}
                  variant="outlined"
                  fullWidth
                >
                  <OutlinedInput
                    value={values.search}
                    labelWidth={105}
                    margin="none"
                    variant="outlined"
                  />
                </FormField>
              </div>
              <div className={classes.groupWrapper}>
                <FormField
                  onBlur={setFieldTouched}
                  onChange={setFieldValue}
                  errors={errors}
                  touched={touched}
                  setSubmitting={setSubmitting}
                  name="group"
                  label={intl.formatMessage(messages.surveyGroup)}
                  variant="outlined"
                  fullWidth
                >
                  <Select
                    value={values.group}
                    input={(
                      <OutlinedInput
                        labelWidth={100}
                        name="group"
                        id="group-picker"
                      />
                    )}
                  >
                    {
                      groups.map((el) => (
                        <MenuItem value={el.id} key={el.id}>{el.name}</MenuItem>
                      ))
                    }
                  </Select>
                </FormField>
              </div>
              <div className={classes.statusWrapper}>
                <FormField
                  onBlur={setFieldTouched}
                  onChange={setFieldValue}
                  errors={errors}
                  touched={touched}
                  setSubmitting={setSubmitting}
                  name="status"
                  label={intl.formatMessage(messages.surveyStatus)}
                  variant="outlined"
                  fullWidth
                >
                  <Select
                    value={values.status}
                    input={(
                      <OutlinedInput
                        labelWidth={100}
                        name="status"
                        id="status-picker"
                      />
                    )}
                  >
                    {
                      statuses.map((el) => (
                        <MenuItem value={el.id} key={el.id}>{el.name}</MenuItem>
                      ))
                    }
                  </Select>
                </FormField>
              </div>
              <div className={classes.surveyRespondentWrapper}>
                <FormField
                  onBlur={setFieldTouched}
                  onChange={setFieldValue}
                  errors={errors}
                  touched={touched}
                  setSubmitting={setSubmitting}
                  name="surveyRespondent"
                  label={intl.formatMessage(messages.surveyRespondent)}
                  variant="outlined"
                  fullWidth
                >
                  <Select
                    value={values.surveyRespondent}
                    input={(
                      <OutlinedInput
                        labelWidth={90}
                        name="surveyRespondent"
                        id="surveyRespondent-picker"
                      />
                    )}
                  >
                    {
                      surveyRespondents.map((el) => (
                        <MenuItem value={el.id} key={el.id}>{el.name}</MenuItem>
                      ))
                    }
                  </Select>
                </FormField>
              </div>
              <div className={classes.surveyTypeWrapper}>
                <FormField
                  onBlur={setFieldTouched}
                  onChange={setFieldValue}
                  errors={errors}
                  touched={touched}
                  setSubmitting={setSubmitting}
                  name="surveyType"
                  label={intl.formatMessage(messages.surveyType)}
                  variant="outlined"
                  fullWidth
                >
                  <Select
                    value={values.surveyType}
                    input={(
                      <OutlinedInput
                        labelWidth={55}
                        name="surveyType"
                        id="surveyType-picker"
                      />
                    )}
                  >
                    {
                      surveyTypes.map((el) => (
                        <MenuItem value={el.id} key={el.id}>{el.name}</MenuItem>
                      ))
                    }
                  </Select>
                </FormField>
              </div>
              <div className={classes.buttonsWrapper}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={!dirty || isSubmitting || !_isEmpty(errors)}
                >
                  {intl.formatMessage(messages.filter)}
                </Button>
                <div className={classes.resetWrapper}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={(event) => {
                      handleReset(event);
                      onSearchSubmit({});
                    }}
                    disabled={!(submitCount || dirty)}
                  >
                    {intl.formatMessage(messages.reset)}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}
      />
      <div className={classes.contentWrapper}>
        {
          items.length > 0 ? (
            <React.Fragment>
              <Table
                stickyHeader
                className={classes.root}
              >
                <TableHead
                  rows={head}
                  sortingDirection={sortingDirection}
                  sortingField={sortingField}
                  onChangeSort={onChangeSort}
                />
                <TableBody>
                  {items.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell noWrap component="th" scope="row" className={classes.numberCell}>
                        {row.id}
                      </TableCell>
                      <TableCell className={classes.idCell}>{row.apiId}</TableCell>
                      <TableCell className={classes.nameCell}>{row.name}</TableCell>
                      <TableCell className={classes.surveyRespondentCell}>
                        {getFromMeta(row.surveyRespondent, surveyRespondents)}
                      </TableCell>
                      <TableCell className={classes.surveyTypeCell}>
                        {getFromMeta(row.surveyType, surveyTypes)}
                      </TableCell>
                      <TableCell
                        className={classNames(classes.roleWrapper, classes.groupCell)}
                      >
                        {getFromMeta(row.surveyGroup, groups)}
                      </TableCell>
                      <TableCell className={classes.statusCell}>
                        {getFromMeta(row.status, statuses)}
                      </TableCell>
                      <TableCell className={classes.questionsCountCell}>
                        {row.questionsCount}
                      </TableCell>
                      <TableCell className={classes.maxtimeCell}>
                        {row.maxTime / 60}
                      </TableCell>
                      <TableCell className={classNames(classes.buttonCell, classes.actionsCell)}>
                        <div className={classes.actionsWrapper}>
                          <Tooltip title={intl.formatMessage(messages.deleteTooltip)} placement="top">
                            <IconButton onClick={() => onDelete(row.apiId)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={intl.formatMessage(messages.previewTooltip)} placement="top">
                            <IconButton onClick={() => onPreview(row.apiId)}>
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={intl.formatMessage(messages.createTooltip)} placement="top">
                            <IconButton onClick={() => onCopy(row.apiId)}>
                              <FileCopyIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={intl.formatMessage(messages.resultsTooltip)} placement="top">
                            <IconButton onClick={() => onDownloadResults(row.apiId)}>
                              <DoneALlIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={intl.formatMessage(messages.cSVTooltip)} placement="top">
                            <IconButton onClick={() => onDownloadCsv(row.apiId)}>
                              <div className={classes.iconButtonText}>
                                {intl.formatMessage(messages.csv)}
                              </div>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={intl.formatMessage(messages.sPSTooltip)} placement="top">
                            <IconButton onClick={() => onDownloadSchema(row.apiId)}>
                              <div className={classes.iconButtonText}>
                                {intl.formatMessage(messages.sps)}
                              </div>
                            </IconButton>
                          </Tooltip>
                          {
                            row.status !== 'NOT_ACTIVE' ? (
                              <Tooltip title={intl.formatMessage(messages.editTooltip)} placement="top">
                                <IconButton onClick={() => onEdit(row.apiId)}>
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            ) : null
                          }
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                ActionsComponent={TablePaginationActions}
                rowsPerPageOptions={rowsPerPageOptions}
                component="div"
                count={totalItemsCount}
                rowsPerPage={rowsPerPage}
                page={page - 1}
                onChangePage={onChangePage}
                onChangeRowsPerPage={onChangeRowsPerPage}
                className={classNames({
                  [classes.caption]: true,
                  [classes.paginationRoot]: true,
                })}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} z ${count}`}
                labelRowsPerPage={intl.formatMessage(messages.rowsPerPage)}
                classes={{
                  select: classes.selectWrapper,
                }}
              />
            </React.Fragment>
          ) : null
        }
      </div>
      <div className={classes.addWrapper}>
        <Fab color="primary" onClick={onAddClick}>
          <AddIcon />
        </Fab>
      </div>
    </React.Fragment>
  );
};

Surveys.propTypes = {
  intl: intlShape.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  totalItemsCount: PropTypes.number.isRequired,
  onAddClick: PropTypes.func.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeRowsPerPage: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDownloadCsv: PropTypes.func.isRequired,
  onDownloadResults: PropTypes.func.isRequired,
  onDownloadSchema: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
  classes: classesShape,
  groups: PropTypes.arrayOf(itemShape),
  initialValues: PropTypes.shape({
    search: PropTypes.string,
    group: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    surveyGroup: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
  sortingDirection: PropTypes.oneOf(['asc', 'desc']),
  sortingField: PropTypes.string,
  statuses: PropTypes.arrayOf(itemShape),
  surveyRespondents: PropTypes.arrayOf(itemShape),
  surveyTypes: PropTypes.arrayOf(itemShape),
  onChangeSort: PropTypes.func,
};

Surveys.defaultProps = {
  classes: {},
  items: [],
  initialValues: {},
  groups: [],
  statuses: [],
  surveyRespondents: [],
  surveyTypes: [],
  sortingField: null,
  sortingDirection: 'asc',
  onChangeSort: () => {},
};

export default withStyles(surveysStyles)(injectIntl(Surveys));
